import { StoreState } from '../../../../store/store.interface';
import {
  isCustomStore,
  isK12Admin,
  isK12Cart,
  ShopCartUrlContext,
  STANDALONE_APPLICATION,
} from '../../../../common/utils/cart-util';
import {
  isHeCustomStoreProduct,
  isHePrivateStoreProduct,
  isNationalProduct,
  isSharpenPlus,
} from '../../../../common/utils/product-util';
import { isTunnelPurchaseFlow, PROFESSIONAL } from '../../../../common/utils/util';
import { getZipCode } from '../../../estimated-tax/estimated-tax.selectors';
import { getPromoCode } from '../../../promo-code/promo-code.selectors';
import { isSuccessUser } from '../../../user-profile/user-profile.selectors';
import { Product, ProductDetails, ProductIdentification, StoreType } from '../interfaces/shop-cart.interface';
import { PaymentOrderItemsProductMetaData } from '../../../order-confirmation/order-completion.interface';
import { isGuestCheckoutPage } from '../../../../common/environment';
import { isBoardsBeyond } from '../../../firstaid-forward/utils/boards-beyond.util';
import { OrderType } from '../../../../common/constants/order-types';
import { determineProgramSourceId, getCoverImage, getGuestCheckoutOrderType, getProductTitle, getProductTypeInfo, getSecondLevelBU, getSharpenOrderType } from '../../../../common/utils/analytics-util';
import { isVirtualLabs, VIRTUAL_LABS_ORDER_TYPE } from '../../../virtual-labs/utils/virtual-labs.util';
import { ActionStatus } from '../../../../common/constants/action-status';

/*----- CREATE SELECTORS ------*/

export const getCartContext = (state: StoreState, storeCode?: string, promoCode?: string): ShopCartUrlContext => ({
  isLoggedIn: isSuccessUser(state),
  storeCode: storeCode ? storeCode : 'national',
  shareCartId: undefined,
  guestCartId: undefined,
  zipCode: getZipCode(state),
  promoCode: promoCode || getPromoCode(state),
  storeType: getStoreType(state)
});

export const getAddToCartResponse = (state: StoreState) => state.shopCartState.getShopCart.data?.addToCartResponse;

export const getAddToCartStatus = (state: StoreState) => state.shopCartState.getShopCart.status;

export const getCartResponse = (state: StoreState) => state.shopCartState.getShopCart;

export const getCart = (state: StoreState) => state.shopCartState.getShopCart.data;

export const getProductsFromCart = (state: StoreState) => state.shopCartState.getShopCart.data?.products;

export const getEstimatedTaxState = (state: StoreState) => state.estimatedTaxState.data;

export const getPromoCodeState = (state: StoreState) => state.promoCodeDiscountState.updatePromoCode.data;

export const getMagentoCartQuoteId = (state: StoreState) => state.getGuestCartTokenMagento.data;

export const getGuestCheckoutEmail = (state: StoreState) => state.shopCartState.getShopCart?.data?.guestCheckoutEmail;

export const getCartForAnalytics = (state: StoreState) => {
  return {
    discount: state.shopCartState.getShopCart.data?.discount,
    orderBusinessUnit: getOrderTypeForAnalytic(state),
    productCount: state.shopCartState.getShopCart.data?.productCount,
    products: state.shopCartState.getShopCart.data?.products?.map((product: Product) => ({
      ...product,
      coverImage: getCoverImage(product)
    })),
    sampleCart: state.shopCartState.getShopCart.data?.sampleCart,
    shippable: state.shopCartState.getShopCart.data?.shippable,
    showPoOption: state.shopCartState.getShopCart.data?.showPoOption,
    totalAmount: state.shopCartState.getShopCart.data?.totalAmount
  }
}

export const getCartStatus = (state: StoreState) => state.shopCartState.getShopCart.status;

export const getCartError = (state: StoreState) => state.shopCartState.getShopCart.error;

export const getCartStore = (state: StoreState) => state.shopCartState.getShopCart.data?.store;

export const getCartErpAccountNumber = (state: StoreState) => state.shopCartState.getShopCart.data?.erpAccountNumber;
/**
 * if any product is unavailable return true, else false.
 * @param state
 * @returns
 */

export const areProductsUnavailable = (state: StoreState) => !isTunnelPurchaseFlow() && !!state.shopCartState.getShopCart.data?.products?.find(prod => !prod.availabilityInfo?.available);

export const isCartInvalid = (state: StoreState) => state.shopCartState.getShopCart.data?.availability === 0;

export const getCartErrorMessage = (state: StoreState) => state.shopCartState.getShopCart.data?.errorMessage;

export const isProfessionalHasMixedCart = (state: StoreState) => {
  const cart = state.shopCartState.getShopCart.data;
  if (cart?.orderBusinessUnit === PROFESSIONAL) {
    const nonProfessionlProducts = cart?.products.filter(product => product.productIdentifier.businessUnit !== PROFESSIONAL);
    if (nonProfessionlProducts?.length > 0) {
      return true;
    }
  }
  return false;
};

export const isCartLoadingOrIdle = (state: StoreState): boolean => {
  return [ActionStatus.idle, ActionStatus.loading].includes(getCartStatus(state));
};

/**
 * @returns true if all non private or custom store product
 */
export const isNationalCart = (state: StoreState) => state.shopCartState.getShopCart.data?.products?.some(prod => isNationalProduct(prod));

export const isHeCustomStoreCart = (state: StoreState) => !(state.shopCartState.getShopCart.data?.products?.some(prod => !isHeCustomStoreProduct(prod)) || isCartEmpty(state));

export const isK12CustomStoreCart = (state: StoreState) => isCustomStore(getCartStore(state)) && isK12Cart(getCart(state));

export const isK12CustomStoreAdmin = (state: StoreState) => isK12CustomStoreCart(state) && isK12Admin(getCart(state)) && isB2bK12Storefront(state);

/**
 * @returns true if no national products and at least one private store product
 */
export const isHePrivateStoreCart = (state: StoreState) => !isNationalCart(state) && state.shopCartState.getShopCart.data?.products?.some(prod => isHePrivateStoreProduct(prod));

export const isCartEmpty = (state: StoreState) => (state.shopCartState.getShopCart.data?.products?.length ?? 0) === 0;

export const isProfessionalCart = (state: StoreState) => state.shopCartState.getShopCart.data?.orderBusinessUnit?.toLowerCase() === 'professional';

export const isHeCart = (state: StoreState) => state.shopCartState.getShopCart.data?.orderBusinessUnit?.toLowerCase() === 'he';

export const isSegCart = (state: StoreState) => state.shopCartState.getShopCart.data?.orderBusinessUnit?.toLowerCase() === 'seg';

export function isCartValid(state) {
  return getCart(state)! && !isCartEmpty(state) && !areProductsUnavailable(state) && !isCartInvalid(state);
}

export const getCartType = (state: StoreState) => {
  if (isTunnelPurchaseFlow()) {
    const cart = getCart(state);
    if(cart?.products.length){
      const product: Product = cart?.products[0]!;
      return getSharpenOrderType({
        offerType: product.productMetadata?.offerType,
        itemType: product.productMetadata?.itemType,
        flow: product.productMetadata?.flow,
        application: product.productMetadata?.application,
        formatCode: product.productMetadata?.formatCode,
        isConnectSharpenBundle: product.productIdentifier.isConnectSharpenBundle,
        isSharpenCompanion: product.productIdentifier.isSharpenCompanion,
        isSharpenAdvantage: product.productIdentifier.isSharpenAdvantage,
      });
    }
  }
  if (isGuestCheckoutPage()) {
    return getGuestCheckoutOrderType(getCartProductMetadata(state)?.application);
  } else if (isHePrivateStoreCart(state)) {
    return OrderType.HE_CUSTOM_STORE;
  } else if (isHeCustomStoreCart(state)) {
    return OrderType.HE_CREATE;
  } else if (isK12CustomStoreCart(state)) {
    return OrderType.K12_STOREFRONT;
  } else if (isNationalCart(state) && isHeCart(state)) {
    return OrderType.HE_NATIONAL;
  } else if (isNationalCart(state) && isSegCart(state)) {
    return OrderType.SEG_NATIONAL;
  } else if(isProfessionalCart(state)) {
    if (isBoardsBeyond()) {
      return OrderType.BOARDS_AND_BEYOND_ORDER_TYPE;
    }
    return OrderType.PROFESSIONAL;
  } else {
    return '';
  }
}


export function getOrderTypeTunnelFlow(
  details:
    | ProductDetails
    | PaymentOrderItemsProductMetaData
    | {
        application?: string;
        itemType?: string;
        isSharpenCompanion?: boolean;
        offerType?: string;
        flow?: string;
      }
) {
  if (details.application && details.application === STANDALONE_APPLICATION) {
    if (details.itemType === 'goldy' || details.flow === 'goldy') {
      return details.isSharpenCompanion ? 'mhgo-sharpen-companion' : 'mhgo';
    }

    if (isVirtualLabs(details.itemType, details.offerType, details.flow)) {
      return VIRTUAL_LABS_ORDER_TYPE;
    }

    return details.itemType?.toLowerCase() ?? details.offerType?.toLowerCase();
  }

  return details.application;
}

export const getCartStoreCatalog = (state: StoreState) => isHePrivateStoreCart(state) ?
  state.shopCartState.getShopCart.data?.products?.find(prod => !!prod.productIdentifier.catalog)?.productIdentifier.catalog : undefined;

export const getErpAccountNumber = (state: StoreState) => state.shopCartState.getShopCart.data?.erpAccountNumber || '';

export const getOrderTypeForAnalytic = (state: StoreState) => state.shopCartState.getShopCart.data?.orderBusinessUnit;

export const getDiscount = (state: StoreState) => state.shopCartState.getShopCart.data?.discount;

export const getProductsForAnalytic = (state: StoreState) =>
  state.shopCartState.getShopCart.data?.products.map((product) => ({
    edition: product?.productMetadata?.edition,
    editionOrRelease: product?.productMetadata?.editionOrRelease,
    type: product?.productMetadata?.productType,
    author: product?.productMetadata?.contributors,
    course: '',
    copyrightYear: product?.productMetadata?.copyrightYear,
    isbn10: product?.productIdentifier?.isbn10,
    isbn13: product?.productIdentifier?.isbn13,
    mhid: product?.productIdentifier?.mhid,
    businessUnit: product?.productIdentifier?.businessUnit,
    ...getProductTitle({
      product,
      captureProductTitle: true,
    }),
    format: product?.productMetadata?.format,
    heebook: product?.heebook,
    ...(product?.productIdentifier?.businessUnit === PROFESSIONAL &&
      product?.productMetadata?.titleType !== undefined && {
        titleType: product.productMetadata.titleType,
      }),
    ...(product?.productIdentifier?.businessUnit === PROFESSIONAL &&
      product?.productMetadata?.division !== undefined && {
        division: product.productMetadata.division,
      }),
    ...getSecondLevelBU({
      programId: product?.productIdentifier.programSourceId ?? '',
    }),
    ...getProductTypeInfo({
      programId: product?.productIdentifier.programSourceId ?? '',
    }),
  }));

export const getStoreType = (state: StoreState) => state.shopCartState.getShopCart.data?.storeType;
export const isB2bK12Storefront = (state: StoreState) => state.shopCartState.getShopCart.data?.storeType === StoreType.B2B;
export const isB2cK12Storefront = (state: StoreState) => state.shopCartState.getShopCart.data?.storeType === StoreType.B2C;

// magento
export const getGuestCartToken = (state) => state.shopCartState.getGuestCartTokenMagento?.data;
export const getLoggedInCartQuoteId = (state) => state.shopCartState.createLoggedInCartQuoteIdMagento?.data;
export const getItemId = (state: StoreState, sku?: string, productIdentifier?: ProductIdentification) =>
  state.shopCartState.getShopCart.data?.products.find(product => isMatchingCartProduct(product, sku, productIdentifier))?.item_id;

function isMatchingCartProduct(product: Product, sku?: string, productIdentifier?: ProductIdentification) {
  if (sku && sku === product.productIdentifier.sku) {
    return true;
  }

  if (!productIdentifier) {
    return false;
  }

  return (
    (!!productIdentifier.offerPricingId && productIdentifier.offerPricingId === product.productIdentifier.offerPricingId) ||
    (!!productIdentifier.isbn13 && productIdentifier.isbn13 === product.productIdentifier.isbn13)
  );
}
export const isMagentoCartItemsShippable = (items) => items.filter(item => item.extension_attributes.digital == '0').length > 0;
export const getProductCurrencyCode = (state: StoreState) => state.shopCartState.getShopCart.data?.products?.[0]?.priceInfo.currencyCode || 'USD';
export const getProductCountryCode = (state: StoreState) => state.shopCartState.getShopCart.data?.products?.[0]?.priceInfo.countryCode || 'US';
export const getOrderBusinessCode = (state: StoreState) => state.shopCartState.getShopCart.data?.orderBusinessUnit;
export const getIntlTaxAmount = (state: StoreState) => state.shopCartState.getShopCart.data?.intlTaxAmount;
export const getShopCartApplication = (state: StoreState) => state.shopCartState.getShopCart.data?.products?.[0]?.productMetadata?.application;
export const getCartProductMetadata = (state: StoreState) => state.shopCartState.getShopCart.data?.products?.[0]?.productMetadata!;
export const getCartProductIdentifier = (state: StoreState) => state.shopCartState.getShopCart.data?.products?.[0]?.productIdentifier!;
export const getBulkAddToCartStatus = (state: StoreState) => state.shopCartState.bulkAddToCart.status;
export const getQuoteIdFromCartProducts = (state: StoreState) => state.shopCartState.getShopCart.data?.products?.[0]?.quoteId;
export const getIfSharpenPlusAvailableInCart = (state: StoreState) =>
  state.shopCartState.getShopCart.data?.products.some((product) =>
    isSharpenPlus(product.productIdentifier.programSourceId)
  );

// tunnel
export const getTunnelExitLink = (state: StoreState) => state.shopCartState.getShopCart.data?.products?.[0]?.exitUrl || state.orderCompletionState.data?.orderItems?.[0]?.productMetaData?.exitUrl;

export const getProgramIdListForAnalytic = (state: StoreState) =>
  state.shopCartState.getShopCart.data?.products.map((product) => (
    determineProgramSourceId(
      product?.productIdentifier?.isbn10!,
      product?.productIdentifier?.isbn13!,
      product?.productIdentifier?.businessUnit,
      product?.productIdentifier.programSourceId ?? '',
    )
  )
);

export const getHeInstructorInvalidProductsRemoved = (state: StoreState) => state.shopCartState.heInstructorInvalidProductsRemoved;
export const getMergeGuestCartCallSucceeded = (state: StoreState) => state.shopCartState.mergeGuestCart.status === ActionStatus.succeeded;
